import React, { useCallback, useRef, useState } from "react";
import { Plural, Trans } from "@lingui/macro";
import { worldLocationLabelFromPort } from "src/utils/worldLocationLabel";
import * as styles from "./CruiseItineraryItem.module.scss";
import { Button, Icon } from "@silversea-ssc/ui-components-react";
import CruiseItineraryItemDetails from "./CruiseItineraryItemDetails";
import DateOnlyFormat from "components/DateOnlyFormat";
import { OpenExcursionModal } from "./CruiseItinerary";
import { ResponsiveSanityImage } from "@silversea-ssc/ui-components-react";
import { formatArrivalDepartureTime } from "./Utils";
import { CategorisedShorex } from "./types";
import {
  RemoteLandProgramme,
  CruiseItineraryItemWithExcursions
} from "./types";

export interface CruiseItineraryItemProps {
  item: CruiseItineraryItemWithExcursions;
  openExcursionModal: OpenExcursionModal;
  portSlideshowIndex: number;
  hideShorexes: boolean;
  shorexes: CategorisedShorex[];
  landProgrammes: RemoteLandProgramme[];
}

const CruiseItineraryItem: React.FC<CruiseItineraryItemProps> = ({
  item,
  openExcursionModal,
  portSlideshowIndex,
  hideShorexes,
  shorexes,
  landProgrammes
}) => {
  const [opened, setOpened] = useState(false);

  const port = item.port;
  const worldLocation = port ? worldLocationLabelFromPort(port) : undefined;

  let portImage;
  if (port && port.portImages) {
    portImage =
      port && port.portImages && port.portImages.length > 0
        ? port.portImages[portSlideshowIndex % port.portImages.length]
        : undefined;
    if (!portImage) portImage = port.portImages[0];
  }

  const detailsRef = useRef<HTMLDivElement | null>(null);
  const toggleDetails = useCallback(
    (
      e:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.KeyboardEvent<HTMLDivElement>
    ) => {
      e.preventDefault();
      if (
        !detailsRef?.current ||
        !detailsRef.current.contains(e.target as Node)
      ) {
        setOpened(!opened);
      }
      //setOpened(!opened);
    },
    [opened]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.code == "Space" || e.code == "Enter") toggleDetails(e);
    },
    [toggleDetails]
  );

  return (
    <div
      className={styles.item}
      onClick={toggleDetails}
      onKeyDown={onKeyDown}
      role="button"
      aria-pressed={opened}
      tabIndex={0}
      style={{ cursor: "pointer" }}
      data-content-piece="details_button"
    >
      <div className={styles.dayNumber}>
        <span>{item.dayNumber?.toString().padStart(2, "0")}</span>
        <span className={styles.stopNameInline}>
          {port?.data?.name?.localized}
        </span>
      </div>

      <div className={styles.portImageContainer}>
        {portImage?.asset && (
          <ResponsiveSanityImage
            image={portImage}
            className={styles.portImage}
            alt={port?.data?.name?.localized}
            defaultWidth={175}
            defaultHeight={160}
          />
        )}
      </div>
      <div className={styles.name}>
        <h3 className={styles.stopName}>{port?.data?.name?.localized}</h3>
        <div className={styles.stopWorldLocation}>{worldLocation}</div>
        {!hideShorexes && (shorexes.length > 0 || landProgrammes?.length > 0) && (
          <div className={styles.excursions}>
            {shorexes.length > 0 && (
              <Plural
                value={shorexes.length}
                one="# Excursion"
                other="# Excursions"
              />
            )}
            {shorexes.length > 0 && landProgrammes.length > 0 && (
              <React.Fragment>
                {" "}
                <Trans>and</Trans>{" "}
              </React.Fragment>
            )}
            {landProgrammes.length > 0 && (
              <Plural
                value={landProgrammes.length}
                one="# Land Programme"
                other="# Land Programmes"
              />
            )}
          </div>
        )}
      </div>
      <div className={styles.dateTime}>
        <DateOnlyFormat
          value={item.date}
          format={{
            month: "long",
            day: "numeric",
            weekday: "short"
          }}
        />{" "}
        <div>
          {formatArrivalDepartureTime(item.arrivalTime, item.departureTime)}
          {item.isOvernight && (
            <Icon
              size="1.5x"
              className={styles.overnightIcon}
              padding="inline"
              name="evening-excursions"
            />
          )}
        </div>
      </div>
      <div className={styles.expand}>
        <Button
          renderAs="div"
          variant="link"
          iconName={opened ? "chevron-up" : "chevron-down"}
          //onClick={() => toggleDetails}
        />
      </div>
      {opened && (
        <div className={styles.details} ref={detailsRef}>
          <CruiseItineraryItemDetails
            hideShorexes={hideShorexes}
            item={item}
            shorexes={shorexes}
            landProgrammes={landProgrammes}
            openExcursionModal={openExcursionModal}
          />
        </div>
      )}
    </div>
  );
};

export default CruiseItineraryItem;
